
import React, { useState, useEffect } from 'react';
import { ArrowLeft, BookOpen, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Navigation from '@/components/layout/Navigation';
import LessonList from '@/components/lessons/LessonList';
import LessonForm from '@/components/lessons/LessonForm';
import { Link, Navigate } from 'react-router-dom';
import { toast } from '@/lib/toast';
import { useAuth } from '@/contexts/AuthContext';
import { lessonService } from '@/services';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Lesson } from '@/models/Lesson';

const Lessons = () => {
  const { user, isAdmin } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddLessonForm, setShowAddLessonForm] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | undefined>(undefined);
  const queryClient = useQueryClient();
  
  // Redirect non-admin users
  if (!isAdmin) {
    toast.error('Only administrators can access lesson management');
    return <Navigate to="/home" replace />;
  }
  
  // Fetch all lessons from Firestore (admin can see all)
  const { data: lessons = [], isLoading } = useQuery({
    queryKey: ['lessons'],
    queryFn: () => lessonService.getAllLessons(),
    enabled: !!user && isAdmin,
  });
  
  // Add mutation
  const addLessonMutation = useMutation({
    mutationFn: (lesson: Omit<Lesson, 'id'>) => {
      if (!user) return Promise.resolve(null);
      return lessonService.addLesson(user.id, lesson);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
  
  // Update mutation
  const updateLessonMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<Lesson> }) => 
      lessonService.updateLesson(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
  
  // Delete mutation
  const deleteLessonMutation = useMutation({
    mutationFn: (lessonId: string) => lessonService.deleteLesson(lessonId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
  
  // Reorder mutation
  const reorderLessonsMutation = useMutation({
    mutationFn: (reorderedLessons: Lesson[]) => 
      lessonService.updateLessonsOrder(reorderedLessons),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
    },
  });
  
  // Filter lessons based on search query
  const filteredLessons = lessons.filter(lesson => {
    return lesson.name.toLowerCase().includes(searchQuery.toLowerCase());
  });
  
  const handleAddLesson = () => {
    setEditingLesson(undefined);
    setShowAddLessonForm(true);
  };
  
  const handleEditLesson = (lesson: Lesson) => {
    setEditingLesson(lesson);
    setShowAddLessonForm(true);
  };
  
  const handleDeleteLesson = (lessonId: string) => {
    deleteLessonMutation.mutate(lessonId);
  };
  
  const handleSaveLesson = (lessonData: Lesson) => {
    if (editingLesson) {
      // Update existing lesson
      const { id, ...updates } = lessonData;
      updateLessonMutation.mutate({ id, updates });
    } else {
      // Add new lesson
      const { id, ...newLesson } = lessonData;
      addLessonMutation.mutate(newLesson);
    }
  };
  
  const handleToggleActive = (lesson: Lesson, active: boolean) => {
    updateLessonMutation.mutate({ 
      id: lesson.id, 
      updates: { active } 
    });
  };
  
  const handleReorderLessons = (reorderedLessons: Lesson[]) => {
    reorderLessonsMutation.mutate(reorderedLessons);
  };
  
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <div className="container max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <header className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <Link to="/home">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-6 w-6" />
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-primary" />
              <span className="font-medium">Lesson Management</span>
            </div>
            <Button variant="outline" size="icon" onClick={handleAddLesson}>
              <Plus className="h-5 w-5" />
            </Button>
          </div>
        </header>
        
        {/* Search */}
        <div className="mb-6 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search lessons..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
        
        {/* Loading state */}
        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-pulse">Loading lessons...</div>
          </div>
        ) : (
          /* Lesson List */
          <LessonList
            lessons={filteredLessons}
            onAddLesson={handleAddLesson}
            onEditLesson={handleEditLesson}
            onDeleteLesson={handleDeleteLesson}
            onToggleActive={handleToggleActive}
            onReorderLessons={handleReorderLessons}
          />
        )}
        
        {/* Add/Edit Lesson Form */}
        <LessonForm
          isOpen={showAddLessonForm}
          onClose={() => setShowAddLessonForm(false)}
          onSave={handleSaveLesson}
          editLesson={editingLesson}
        />
      </div>
      
      <Navigation />
    </div>
  );
};

export default Lessons;
